import { useEffect } from 'react';
import { Modal, Form, InputNumber, Select, DatePicker, Input, Button, Row, Col, Typography, Alert, Spinner, message, QRCode } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderNo?: string;
}

const MODES = ['UPI', 'NEFT', 'RTGS', 'IMPS', 'CASH', 'CHEQUE', 'CARD', 'OTHER'];
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Retailer (or SP/SH on behalf) pays for an ORDER. Submits a PENDING payment linked to the order —
 * an accountant approves it before it posts to the ledger. No invoice allocation.
 */
export function PayOrderModal({ open, onClose, orderId, orderNo }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const amount = Form.useWatch('amount', form);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['order-pay-summary', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/payment-summary` }),
    enabled: open && !!orderId,
  });
  const { data: pay } = useQuery({
    queryKey: ['company-pay-info'],
    queryFn: () => request<any>({ url: '/company/bank-accounts/pay-info' }),
    enabled: open,
  });
  // How much account credit (advance-in-credit) can be applied to this order right now. Returns 0 when the
  // feature is off, so the whole credit UI simply doesn't show.
  const { data: creditPreview } = useQuery({
    queryKey: ['order-credit-preview', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/credit-preview` }),
    enabled: open && !!orderId,
  });

  const due = Number(summary?.due ?? 0);
  const applicable = Math.round(Number(creditPreview?.applicable ?? 0) * 100) / 100;
  const hasCredit = applicable > 0.009;
  const netToPay = Math.max(0, Math.round((due - applicable) * 100) / 100);
  const fullyCovered = hasCredit && netToPay <= 0.009;

  useEffect(() => {
    if (open && summary) {
      form.setFieldsValue({ amount: netToPay > 0 ? netToPay : undefined, mode: 'UPI', paymentDate: dayjs() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, summary, applicable]);

  const invalidateAll = () => {
    for (const key of [['orders'], ['order', orderId], ['order-pay-summary', orderId], ['order-credit-preview', orderId], ['order-payments', orderId], ['payments'], ['badges'], ['retailer']]) {
      qc.invalidateQueries({ queryKey: key as any });
    }
  };
  // Apply the retailer's account credit to this order (ledger-neutral) — used both for zero-cash checkout and
  // just before a cash payment covers the remaining net.
  const applyCredit = useMutation({
    mutationFn: () => request<any>({ url: `/orders/${orderId}/apply-credit`, method: 'POST' }),
  });
  const settleWithCredit = async () => {
    try {
      const r = await applyCredit.mutateAsync();
      message.success(`₹${Number(r.applied ?? applicable).toLocaleString('en-IN')} credit applied — order settled`);
      invalidateAll();
      onClose();
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Failed to apply credit');
    }
  };

  const submit = useMutation({
    mutationFn: (v: any) => request<any>({
      url: `/orders/${orderId}/pay`, method: 'POST',
      data: {
        paymentDate: v.paymentDate.format('YYYY-MM-DD'),
        amount: v.amount, mode: v.mode, referenceNo: v.referenceNo, utr: v.utr,
        attachmentUrls: v.attachmentUrls,
      },
    }),
    onSuccess: (r) => {
      message.success(`Payment ${r.paymentNo} submitted — pending approval`);
      invalidateAll();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to submit payment'),
  });
  // Cash-with-credit: apply the available credit first (ledger-neutral), then submit the cash for the net.
  const handleFinish = async (v: any) => {
    if (hasCredit) {
      try { await applyCredit.mutateAsync(); } catch (e: any) { message.error(e.response?.data?.error?.message ?? 'Failed to apply credit'); return; }
    }
    submit.mutate(v);
  };

  const upiString = pay?.upiString as string | undefined;
  const qrValue = upiString ? `${upiString}${amount ? `&am=${Number(amount).toFixed(2)}` : ''}` : '';

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Pay — Order ${orderNo ?? ''}`} width={680}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={hasCredit ? 6 : 8}><Typography.Text type="secondary">Order total</Typography.Text><div style={{ fontSize: 16, fontWeight: 600 }}>{inr2(summary?.grandTotal)}</div></Col>
            <Col span={hasCredit ? 6 : 8}><Typography.Text type="secondary">Paid</Typography.Text><div style={{ fontSize: 16, fontWeight: 600, color: '#1F8A4C' }}>{inr2(summary?.paid)}</div></Col>
            {hasCredit && <Col span={6}><Typography.Text type="secondary">Credit applied</Typography.Text><div style={{ fontSize: 16, fontWeight: 600, color: '#1F8A4C' }}>−{inr2(applicable)}</div></Col>}
            <Col span={hasCredit ? 6 : 8}><Typography.Text type="secondary">{hasCredit ? 'Net to pay' : 'Due'}</Typography.Text><div style={{ fontSize: 16, fontWeight: 700, color: '#D9363E' }}>{inr2(netToPay)}</div></Col>
          </Row>

          {Number(summary?.pendingAmt) > 0 && (
            <Alert type="info" style={{ marginBottom: 12 }} title={`${inr2(summary?.pendingAmt)} already submitted and awaiting approval.`} />
          )}

          {hasCredit && (
            <Alert type="success" style={{ marginBottom: 12 }} title={
              fullyCovered
                ? `You have ${inr2(applicable)} account credit — it covers this order in full, no cash needed.`
                : `${inr2(applicable)} of your account credit will be applied to this order — you pay only ${inr2(netToPay)}.`
            } />
          )}

          {due <= 0.01 ? (
            <Alert type="success" title="This order is fully paid." />
          ) : fullyCovered ? (
            <Button variant="primary" block loading={applyCredit.isPending} onClick={settleWithCredit}>
              Settle order with {inr2(applicable)} account credit
            </Button>
          ) : (
            <Form form={form} layout="vertical" onFinish={handleFinish}>
              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Row gutter={12}>
                    <Col span={12}><Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={1} max={netToPay} step={100} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item></Col>
                    <Col span={12}><Form.Item name="mode" label="Mode" rules={[{ required: true }]}><Select options={MODES.map((m) => ({ value: m }))} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="referenceNo" label="Reference No"><Input placeholder="UPI / cheque ref" /></Form.Item></Col>
                    <Col span={24}><Form.Item name="utr" label="UTR / Bank Ref"><Input /></Form.Item></Col>
                    <Col span={24}>
                      <Form.Item
                        name="attachmentUrls"
                        label="Payment proof"
                        rules={[{ required: true, type: 'array', min: 1, message: 'Upload your payment screenshot / receipt' }]}
                        extra="Required — the UPI/transfer screenshot or the receipt."
                      >
                        <MultiFileUploadField category="receipt" buttonLabel="Upload proof" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={10}>
                  <div style={{ textAlign: 'center', border: '1px dashed #d9d9d9', borderRadius: 8, padding: 12 }}>
                    <Typography.Text strong>Scan to Pay (UPI)</Typography.Text>
                    <div style={{ margin: '10px 0', display: 'flex', justifyContent: 'center' }}>
                      {qrValue ? (
                        <QRCode value={qrValue} size={150} />
                      ) : pay?.upiQrUrl ? (
                        <img src={pay.upiQrUrl} width={150} height={150} style={{ objectFit: 'contain' }} alt="UPI QR" />
                      ) : (
                        <Typography.Text type="secondary">UPI not configured</Typography.Text>
                      )}
                    </div>
                    {pay?.upiId && <div style={{ fontSize: 12 }}><b>UPI:</b> {pay.upiId}</div>}
                    {pay?.bankName && <div style={{ fontSize: 11, color: '#888' }}>{pay.bankName} · A/c {pay.accountNumber} · {pay.ifsc}</div>}
                  </div>
                </Col>
              </Row>
              <Button variant="primary" type="submit" loading={submit.isPending || applyCredit.isPending} block style={{ marginTop: 4 }}>
                {hasCredit ? `Apply ${inr2(applicable)} credit & submit ${inr2(netToPay)}` : 'Submit payment for approval'}
              </Button>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0, textAlign: 'center' }}>
                Your payment is recorded only after an accountant verifies the proof.
              </Typography.Paragraph>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
}
